/**
 * definition-to-graph.ts
 *
 * Pure transform: Keystone workflow DSL → React Flow nodes + edges.
 *
 * Node kinds and their colours:
 *   evaluate         — blue   (#3b82f6)
 *   update_entity    — green  (#16a34a)
 *   wait_for_decision— amber  (#d97706)
 *   pass             — grey   (#6b7280)
 *   fail             — red    (#dc2626)
 *
 * Edges:
 *   sequence  — solid arrow (step n → step n+1)
 *   guard     — dashed arrow labelled with the `when` expression
 *
 * Input/output handles:
 *   Each node exposes a "source" handle (bottom) and a "target" handle (top).
 *   evaluate steps additionally show the result_path as an output label.
 *   update_entity steps show the set fields as an input label.
 *   wait_for_decision steps show the options as output labels.
 */

export interface WorkflowStep {
  name: string;
  kind: "evaluate" | "update_entity" | "wait_for_decision" | "pass" | "fail";
  when?: string;
  // evaluate
  expression?: string;
  result_path?: string;
  // update_entity
  entity_id?: string;
  set?: Record<string, unknown>;
  // wait_for_decision
  decider?: string;
  options?: string[];
  timeout?: string;
  timeout_decision?: string;
  // pass
  comment?: string;
  // fail
  error?: string;
  cause?: string;
}

export interface WorkflowDefinition {
  workflow: string;
  version?: number;
  trigger?: {
    on: string;
    entity_type?: string;
  };
  steps: WorkflowStep[];
}

export interface GraphNodeData {
  label: string;
  kind: WorkflowStep["kind"];
  color: string;
  textColor: string;
  details: string[];
  when?: string;
  isGuarded: boolean;
  execStatus?: string;
}

export interface GraphNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: GraphNodeData;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  animated: boolean;
  style: Record<string, unknown>;
  labelStyle?: Record<string, unknown>;
  labelBgStyle?: Record<string, unknown>;
  type: string;
}



const KIND_STYLES: Record<
  WorkflowStep["kind"],
  { color: string; textColor: string; label: string }
> = {
  evaluate: { color: "#3b82f6", textColor: "#fff", label: "Evaluate" },
  update_entity: { color: "#16a34a", textColor: "#fff", label: "Update Entity" },
  wait_for_decision: { color: "#d97706", textColor: "#fff", label: "Wait for Decision" },
  pass: { color: "#6b7280", textColor: "#fff", label: "Pass" },
  fail: { color: "#dc2626", textColor: "#fff", label: "Fail" },
};

function stepDetails(step: WorkflowStep): string[] {
  const lines: string[] = [];
  switch (step.kind) {
    case "evaluate":
      if (step.expression) lines.push(`expr: ${step.expression}`);
      if (step.result_path) lines.push(`→ ${step.result_path}`);
      break;
    case "update_entity":
      if (step.entity_id) lines.push(`entity: ${step.entity_id}`);
      if (step.set) {
        for (const [k, v] of Object.entries(step.set)) {
          lines.push(`set ${k} = ${String(v)}`);
        }
      }
      break;
    case "wait_for_decision":
      if (step.decider) lines.push(`decider: ${step.decider}`);
      if (step.options) lines.push(`options: ${step.options.join(" | ")}`);
      if (step.timeout) lines.push(`timeout: ${step.timeout}`);
      if (step.timeout_decision) lines.push(`on timeout → ${step.timeout_decision}`);
      break;
    case "pass":
      if (step.comment) lines.push(step.comment);
      break;
    case "fail":
      if (step.error) lines.push(`error: ${step.error}`);
      if (step.cause) lines.push(step.cause);
      break;
  }
  return lines;
}

// Trigger node at the top
function triggerNode(def: WorkflowDefinition): GraphNode {
  const triggerLabel = def.trigger
    ? `${def.trigger.on}${def.trigger.entity_type ? `\n${def.trigger.entity_type}` : ""}`
    : "manual";
  return {
    id: "__trigger__",
    type: "keystoneNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Trigger",
      kind: "pass",
      color: "#7c3aed",
      textColor: "#fff",
      details: [triggerLabel],
      isGuarded: false,
    },
  };
}

export function definitionToGraph(def: WorkflowDefinition): {
  nodes: GraphNode[];
  edges: GraphEdge[];
} {
  const NODE_WIDTH = 240;
  const X_CENTER = 0;
  const Y_GAP = 160;

  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];

  // Trigger node
  const trigger = triggerNode(def);
  nodes.push(trigger);

  const steps = def.steps;

  steps.forEach((step, i) => {
    const style = KIND_STYLES[step.kind] ?? KIND_STYLES.pass;
    const details = stepDetails(step);

    nodes.push({
      id: step.name,
      type: "keystoneNode",
      position: {
        x: X_CENTER,
        y: (i + 1) * Y_GAP,
      },
      data: {
        label: step.name,
        kind: step.kind,
        color: style.color,
        textColor: style.textColor,
        details,
        ...(step.when !== undefined ? { when: step.when } : {}),
        isGuarded: !!step.when,
      },
    });

    // Edge from previous node (trigger or prior step)
    const sourceId = i === 0 ? "__trigger__" : steps[i - 1].name;
    const isGuarded = !!step.when;

    edges.push({
      id: `e-${sourceId}-${step.name}`,
      source: sourceId,
      target: step.name,
      ...(isGuarded ? { label: `when: ${step.when ?? ""}` } : {}),
      animated: isGuarded,
      type: "smoothstep",
      style: {
        ...(isGuarded ? { strokeDasharray: "6 3" } : {}),
        stroke: isGuarded ? "#9ca3af" : "#374151",
        strokeWidth: 2,
      },
      ...(isGuarded ? { labelStyle: { fontSize: 11, fill: "#6b7280" } } : {}),
      ...(isGuarded ? { labelBgStyle: { fill: "#f9fafb", fillOpacity: 0.9, rx: 4, ry: 4 } } : {}),
    });
  });

  // Lay out nodes vertically centred
  nodes.forEach((n, i) => {
    n.position = { x: X_CENTER - NODE_WIDTH / 2, y: i * Y_GAP };
  });

  return { nodes, edges };
}
